import {
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import DeleteIcon from '@mui/icons-material/Delete'

import { AddImpactPeriodButton } from '../add-impact-period-button.component'
import { ImpactPeriodRow } from './impact-period-row.component'
import { useRemoveImpactPeriods, useTableItemsCheck, useUpdateImpactPeriodsOrder } from 'src/hooks'
import { IImpactPeriodBaseWithId } from 'src/types'
import { IFilter } from 'src/components/filter/types/filter.types'

import { createDndHandler } from 'src/common/utils'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface IImpactPeriodsTableProps {
  items?: IImpactPeriodBaseWithId[] | void
  filter: IFilter
}

const ImpactPeriodsTable = ({ items, filter }: IImpactPeriodsTableProps) => {
  const {
    handleAllItemsCheck,
    selectedItemsIds,
    setSelectedItemsIds,
    isAllChecked,
    isIndeterminated,
    isAllUnchecked,
    ...itemsCheckProps
  } = useTableItemsCheck({
    items: items ?? [],
  })
  const theme = useTheme()

  const { mutate: changeOrder, isLoading } = useUpdateImpactPeriodsOrder()
  const { mutateAsync: removeItems } = useRemoveImpactPeriods()

  const { handleDragEnd } = createDndHandler({
    ids: items?.map((item) => item.id) ?? [],
    changeOrder,
  })

  const handleItemsRemove = async () => {
    await removeItems(selectedItemsIds)
    setSelectedItemsIds([])
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <Checkbox
                  indeterminate={isIndeterminated}
                  sx={{ color: theme.palette.custom.grey2 }}
                  onChange={handleAllItemsCheck}
                  checked={isAllChecked}
                />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="impact-periods-name-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="impact-periods-days-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="table-created-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="table-modified-column" />
              </TableCell>
              <TableCell align="center" />
              <TableCell align="center">
                <IconButton disabled={isAllUnchecked} onClick={handleItemsRemove}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="impact-periods-table">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {items?.map((row, index) => (
                    <ImpactPeriodRow
                      {...itemsCheckProps}
                      disableDragging={isLoading || !!filter.query}
                      index={index}
                      key={row.id}
                      row={row}
                    />
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      </TableContainer>
      <AddImpactPeriodButton />
    </>
  )
}

export { ImpactPeriodsTable }
